/**
 * Landing Page V2 - Mobile Navbar
 *
 * Mobile navigation bar with hamburger menu, logo, and profile
 */

'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  typographyColors,
  backgroundColors,
  brandColors,
  spacing,
  typography,
  fontPrimary,
  strokeColors,
  borders,
} from '@/design-system/utils';
import Image from 'next/image';
import { useChatScreenStore } from '@/store/chatScreenStore';
import { useUserStore } from '@/store/userStore';
import { useAuthTokens } from '@/utils/tokenHelper';
import { safeRouterPush } from '@/utils/safeRouterPush';
import { CloseButton } from '@/components/common/CloseButton';
import { MobileNavbarItem, NewChatButton } from './items';
import { ProfileMenu } from '../shared';

interface MenuItem {
  id: string;
  label: string;
  iconName: string;
}

// Main navigation items (with arrows)
const mainMenuItems: MenuItem[] = [
  {
    id: 'experiences',
    label: 'Experiences',
    iconName: 'experiences',
  },
  {
    id: 'discover',
    label: 'Discover',
    iconName: 'discover',
  },
  {
    id: 'blog',
    label: 'Blog',
    iconName: 'blog',
  },
];

// Secondary navigation items (without arrows)
const secondaryMenuItems: MenuItem[] = [
  {
    id: 'history',
    label: 'History',
    iconName: 'history',
  },
  {
    id: 'riders',
    label: 'Riders Corner',
    iconName: 'riders',
  },
];

export interface MobileNavbarProps {
  onNewChat?: () => void;
  onLogoClick?: () => void;
}

export function MobileNavbar({
  onNewChat,
  onLogoClick,
}: MobileNavbarProps = {}) {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string>('discover');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [imageError, setImageError] = useState(false);
  const { conversations, setActiveConversation, getActiveConversation } =
    useChatScreenStore();
  const { name, photo } = useUserStore();
  const { accessToken, refreshToken } = useAuthTokens();

  const isLoggedIn = !!accessToken && !!refreshToken;
  // Show profile initial when we have a real name (from rehydrated store); otherwise 'U'
  const avatarLetter =
    name && name !== 'User Name' ? name.charAt(0).toUpperCase() : 'U';

  // Debug: navbar profile state (filter console by [NAVBAR_DEBUG])
  useEffect(() => {
    console.log('[NAVBAR_DEBUG] MobileNavbar', {
      name,
      hasPhoto: !!photo,
      isLoggedIn,
      avatarLetter,
    });
  }, [name, photo, isLoggedIn, avatarLetter]);

  // Reset image error when photo changes
  useEffect(() => {
    setImageError(false);
  }, [photo]);

  // Get display name for greeting (only show user name when logged in to avoid stale data after auto-logout)
  const isDefaultName = name === 'User Name';
  const displayName = !isLoggedIn
    ? 'Traveller'
    : isDefaultName
      ? 'Traveller'
      : name || 'Traveller';

  // Update active menu based on current route
  useEffect(() => {
    const path = router.pathname;
    if (path === '/experiences') {
      setActiveMenu('experiences');
    } else if (path === '/chat') {
      setActiveMenu('discover');
    } else if (path === '/history') {
      setActiveMenu('history');
    } else if (
      path === '/riders-corner' ||
      path.startsWith('/riders-corner/')
    ) {
      setActiveMenu('riders');
    }
  }, [router.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNewChat = () => {
    console.log('🔄 [MobileNavbar] New Chat button clicked');
    onNewChat?.();
    closeMenu();
  };

  const handleLogoClick = () => {
    onLogoClick?.();
    closeMenu();
  };

  const handleMenuClick = (menuId: string) => {
    setActiveMenu(menuId);
    closeMenu();

    // Clear all filter-related localStorage whenever any navbar item is clicked
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('nexgo-experience-filters');
        localStorage.removeItem('nexgo-experience-temp-filters');
        localStorage.removeItem('nexgo-experience-selected-filter');
        localStorage.removeItem('nexgo-experience-search-query');
      } catch (error) {
        console.warn('Failed to clear filter localStorage:', error);
      }
    }

    // Navigate based on menu item - matching desktop Sidebar behavior
    if (menuId === 'experiences') {
      void safeRouterPush(router, '/experiences');
    } else if (menuId === 'discover') {
      // Check for active conversation first (the one that was there before navigating away)
      const activeConversation = getActiveConversation();

      if (activeConversation && activeConversation.messages.length > 0) {
        // Restore the active conversation
        setActiveConversation(activeConversation.id);
        // Navigate to chat and pass conversation data via query params
        void safeRouterPush(router, {
          pathname: '/chat',
          query: {
            conversationId: activeConversation.id,
            sessionId: activeConversation.sessionId,
          },
        });
      } else if (conversations.length > 0) {
        // Fallback: if no active conversation, use the most recent conversation
        const mostRecentConversation = conversations[0];
        setActiveConversation(mostRecentConversation.id);
        void safeRouterPush(router, {
          pathname: '/chat',
          query: {
            conversationId: mostRecentConversation.id,
            sessionId: mostRecentConversation.sessionId,
          },
        });
      } else {
        // No conversations, just navigate to chat (will show landing page)
        void safeRouterPush(router, '/chat');
      }
    } else if (menuId === 'history') {
      void safeRouterPush(router, '/history');
    } else if (menuId === 'blog') {
      // Navigate to external blog site
      window.open('https://blogs.nexgo.ai/', '_blank', 'noopener,noreferrer');
    } else if (menuId === 'riders') {
      void safeRouterPush(router, '/riders-corner');
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <nav
        style={{
          backgroundColor: backgroundColors.primary,
          paddingTop: spacing[4],
          paddingBottom: spacing[4],
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
        }}
        className='md:hidden fixed top-0 left-0 right-0 flex items-center justify-between z-[100] h-14'
      >
        {/* Hamburger Menu */}
        <button
          onClick={toggleMenu}
          className='w-6 h-6 flex items-center justify-center flex-shrink-0'
          aria-label='Menu'
        >
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
              d='M3 12H21M3 6H21M3 18H21'
              stroke={typographyColors.primary}
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>

        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className='flex items-center justify-center w-[72px] h-6 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0'
          aria-label='Go to landing page'
        >
          <Image
            src='/assets/images/logo.svg'
            alt='Nexgo'
            width={72}
            height={24}
            className='w-full h-full object-contain'
            unoptimized
          />
        </button>

        {/* Profile */}
        <button
          ref={el => {
            setProfileMenuAnchor(el);
          }}
          onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
          className='flex items-center justify-center w-10 h-10 flex-shrink-0'
          aria-label='Profile'
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: photo
                ? 'transparent'
                : `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.bold,
              color: '#1C1917',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            {photo && !imageError ? (
              <Image
                src={photo}
                alt={name || 'User'}
                width={40}
                height={40}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                unoptimized
                onError={() => setImageError(true)}
              />
            ) : (
              avatarLetter
            )}
          </div>
        </button>
      </nav>

      {/* Profile Menu - Rendered outside nav to avoid stacking context issues */}
      <ProfileMenu
        isOpen={isProfileMenuOpen}
        onClose={() => setIsProfileMenuOpen(false)}
        anchorElement={profileMenuAnchor}
        position='bottom'
      />

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className='md:hidden fixed inset-0 bg-black/50 z-[90]'
          onClick={closeMenu}
        />
      )}

      {/* Slide-in Menu */}
      <div
        className={`md:hidden fixed top-0 left-0 h-full w-[280px] z-[100] transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          borderRadius: borders.radius.xl, // 12px - var(--Dimensions-Border-border-extra-large, 12px)
          border: `1px solid ${strokeColors.lightest}`, // var(--Colors-Stroke-Lightest, #F5F5F4)
          background: backgroundColors.primary, // White background
          boxShadow: '0 4px 4px 0 rgba(210, 210, 210, 0.25)',
        }}
      >
        {/* Header Section */}
        <div
          style={{
            backgroundColor: backgroundColors.primary, // White background
            paddingTop: spacing[4],
            paddingBottom: spacing[4],
            paddingLeft: spacing[4],
            paddingRight: spacing[4],
          }}
        >
          {/* Close Button */}
          <CloseButton
            onClick={closeMenu}
            size={16}
            ariaLabel='Close menu'
            variant='filled'
            className='mb-4'
            style={{
              backgroundColor: backgroundColors.secondary, // #f5f5f4
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              padding: spacing[2], // 8px
            }}
          />

          {/* Header Section with Border */}
          <div
            style={{
              border: `1px solid ${backgroundColors.secondary}`, // #f5f5f4
              paddingTop: 0,
              paddingBottom: spacing[4], // 16px
              paddingLeft: 0,
              paddingRight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[8], // 32px gap between header content and New Chat
            }}
          >
            {/* Logo and Greeting */}
            <div className='flex items-center justify-between'>
              <div
                className='flex flex-col'
                style={{
                  gap: spacing[2], // 8px gap between logo and greeting
                }}
              >
                <button
                  onClick={handleLogoClick}
                  className='cursor-pointer hover:opacity-80 transition-opacity'
                  aria-label='Go to landing page'
                  style={{
                    width: '68px',
                    height: '22px',
                  }}
                >
                  <Image
                    src='/assets/images/logo.svg'
                    alt='Nexgo'
                    width={68}
                    height={22}
                    className='h-auto w-auto'
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: '50% 50%',
                    }}
                    unoptimized
                  />
                </button>
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.regular,
                    fontSize: '16px',
                    color: typographyColors.primary,
                    lineHeight: 'normal',
                  }}
                >
                  Hi {displayName}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  width: '52px',
                  height: '52px',
                  padding: spacing[3], // 12px
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: spacing[2], // 8px
                  flexShrink: 0,
                  borderRadius: '9999px',
                }}
              >
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    background: photo
                      ? 'transparent'
                      : `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: fontPrimary,
                    fontSize: typography.size.lg,
                    fontWeight: typography.weight.bold,
                    color: '#1C1917',
                    minWidth: '52px',
                    minHeight: '52px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  {photo && !imageError ? (
                    <Image
                      src={photo}
                      alt={name || 'User'}
                      width={52}
                      height={52}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      unoptimized
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    avatarLetter
                  )}
                </div>
              </div>
            </div>

            {/* New Chat Button */}
            <button
              onClick={handleNewChat}
              style={{
                display: 'none',
                padding: `${spacing[2.5]} ${spacing[1.5]}`, // 10px top/bottom, 6px left/right
                alignItems: 'center',
                gap: spacing[1.5], // 6px
                alignSelf: 'stretch',
                borderRadius: '6px',
                border: `1px solid ${brandColors.primary}`, // #FDE047
                background: brandColors.primary, // #FDE047
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              className='hover:opacity-90'
              aria-label='New Chat'
            >
              <NewChatButton variant='icon-only' />
              <span
                style={{
                  color: typographyColors.primary, // var(--Colors-Typography-primary, #1C1917)
                  textAlign: 'center',
                  fontFamily: fontPrimary, // Gantari
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: 'normal',
                }}
              >
                New Chat
              </span>
            </button>
          </div>
        </div>

        {/* Menu Content */}
        <div
          className='flex flex-col overflow-y-auto navbar-scrollbar'
          style={{
            height: 'calc(100vh - 280px)',
            paddingTop: spacing[4],
            paddingBottom: spacing[6],
          }}
        >
          {/* Main Navigation Items */}
          <div
            className='flex flex-col'
            style={{
              paddingTop: spacing[4],
              gap: spacing[1], // 4px gap between items
              paddingLeft: '16px', // Added padding
              paddingRight: '16px', // Added padding
            }}
          >
            {mainMenuItems.map(item => {
              const isActive = activeMenu === item.id;
              return (
                <MobileNavbarItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  iconName={item.iconName}
                  isActive={isActive}
                  onClick={() => handleMenuClick(item.id)}
                  showArrow={true}
                />
              );
            })}
          </div>

          {/* Separator Line */}
          <div
            style={{
              height: '1px',
              backgroundColor: backgroundColors.shade,
              marginTop: spacing[4],
              marginBottom: spacing[4],
              marginLeft: spacing[6],
              marginRight: spacing[6],
            }}
          />

          {/* Secondary Navigation Items */}
          <div
            className='flex flex-col'
            style={{
              gap: spacing[1], // 4px gap between items
              paddingLeft: '16px', // Added padding
              paddingRight: '16px', // Added padding
            }}
          >
            {secondaryMenuItems.map(item => {
              const isActive = activeMenu === item.id;
              return (
                <MobileNavbarItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  iconName={item.iconName}
                  isActive={isActive}
                  onClick={() => handleMenuClick(item.id)}
                  showArrow={false}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
